import { useQuery } from "@tanstack/react-query";
import { Divider, Spinner, Title3 } from "@fluentui/react-components";
import { api } from "../../api/client";

interface AdminDash {
  total: number;
  open: number;
  resolved30d: number;
  closed30d: number;
  breached: number;
  mttrHours: number;
  byCategory: { categoryId: string; categoryName: string; count: number }[];
  byPriority: { priority: string; count: number }[];
}

const PRIORITY_COLOR: Record<string, string> = {
  Critical: "#D13438",
  High: "#F7630C",
  Medium: "#0078D4",
  Low: "#8A8886",
};

export function AdminDashboardPage() {
  const q = useQuery({
    queryKey: ["dashboard", "admin"],
    queryFn: () => api.get<AdminDash>("/api/dashboard/admin"),
  });

  if (q.isLoading) return <div style={{ padding: "40px 0", textAlign: "center" }}><Spinner label="Loading dashboard…" /></div>;
  const d = q.data!;

  const maxCatCount = Math.max(1, ...d.byCategory.map((c) => c.count));
  const maxPriCount = Math.max(1, ...d.byPriority.map((p) => p.count));

  return (
    <div>
      <Title3>Admin Dashboard</Title3>

      {/* KPI stat cards */}
      <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
        <KpiCard value={d.total} label="Total Tickets" color="#323130" />
        <KpiCard value={d.open} label="Open" color="#0078D4" />
        <KpiCard value={d.resolved30d} label="Resolved (30d)" color="#107C10" />
        <KpiCard value={d.closed30d} label="Closed (30d)" color="#8764B8" />
        <KpiCard
          value={d.breached}
          label="SLA Breached"
          color={d.breached > 0 ? "#D13438" : "#8A8886"}
          urgent={d.breached > 0}
        />
        <KpiCard value={`${d.mttrHours}h`} label="MTTR" color="#605E5C" />
      </div>

      <Divider style={{ margin: "28px 0" }} />

      {/* Bottom breakdown grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>

        {/* By Priority */}
        <section>
          <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 600 }}>Open by priority</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(["Critical", "High", "Medium", "Low"] as const).map((pri) => {
              const row = d.byPriority.find((p) => p.priority === pri);
              const count = row?.count ?? 0;
              const pct = Math.round((count / maxPriCount) * 100);
              const color = PRIORITY_COLOR[pri];
              return (
                <div key={pri}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color }}>{pri}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#323130" }}>{count}</span>
                  </div>
                  <div style={{ height: 8, background: "#F3F2F1", borderRadius: 4, overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: color,
                        borderRadius: 4,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* By Category */}
        <section>
          <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 600 }}>By category</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {d.byCategory
              .slice()
              .sort((a, b) => b.count - a.count)
              .slice(0, 8)
              .map((c) => {
                const pct = Math.round((c.count / maxCatCount) * 100);
                return (
                  <div key={c.categoryId}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: "#323130", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "75%" }}>
                        {c.categoryName}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#323130", marginLeft: 8 }}>{c.count}</span>
                    </div>
                    <div style={{ height: 6, background: "#F3F2F1", borderRadius: 3, overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          background: "#0078D4",
                          borderRadius: 3,
                          opacity: 0.7,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      </div>
    </div>
  );
}

function KpiCard({
  value, label, color, urgent,
}: {
  value: number | string;
  label: string;
  color: string;
  urgent?: boolean;
}) {
  return (
    <div style={{
      flex: "1 1 140px",
      minWidth: 140,
      background: urgent ? `${color}08` : "#FFFFFF",
      border: `1px solid ${urgent ? color + "35" : "#EDEBE9"}`,
      borderRadius: 10,
      padding: "16px 18px",
      position: "relative",
      overflow: "hidden",
    }}>
      {urgent && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 3,
          background: color,
          borderRadius: "10px 10px 0 0",
        }} />
      )}
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#605E5C", marginTop: 7 }}>{label}</div>
    </div>
  );
}
